import { useCallback, useMemo } from "react";

import queryString from "query-string";
import { type Location, useLocation, useNavigate } from "react-router-dom";

import {
  downloadPageUrl,
  Environment,
  environment,
  sessionStorageKeys,
  teamsArticleURL,
  teamsMarketingPageURL,
  zendeskTokenUrl,
} from "../config";
import {
  type DeepLinkIntent,
  DeepLinkSearchParam,
  SearchParam,
  type SubscriptionPeriod,
  type SubscriptionTier,
  type URLState,
} from "../types";
import { PageSource } from "../types/tracking";
import { getShaprURLProtocol, sanitizeRedirectUrl, urlWithZendeskAuth } from "../urlUtils";
import useUrlHelper from "./urlHelper";
import { useTracker } from "./useTracker";

const appendParams = (
  path: string,
  search: string,
  additionalSearchParams?: Record<string, string>
) => {
  const query = {
    ...queryString.parse(search),
    ...additionalSearchParams,
  };
  return queryString.stringifyUrl({ url: path, query });
};

const makeQueryParams = (
  location: Location,
  params: {
    additionalQueryParams?: { [K in SearchParam]?: string };
    omitQueryParams?: string[];
  } = {}
) => {
  const { omitQueryParams, additionalQueryParams } = params;

  const queryParams = new URLSearchParams(location.search);
  omitQueryParams?.forEach?.((name) => queryParams.delete(name));
  if (additionalQueryParams) {
    Object.keys(additionalQueryParams).forEach((key) =>
      queryParams.set(key, additionalQueryParams[key as SearchParam] ?? "")
    );
  }
  return queryParams.toString();
};

const useNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlHelper = useUrlHelper();
  const tracker = useTracker();

  const goToSharedWithMe = useCallback(
    ({ teamID, replace }: { teamID: string; replace?: boolean }) => {
      const search = new URLSearchParams(location.search);
      navigate(
        {
          pathname: `/teams/${teamID}/shared-with-me`,
          search: search.toString(),
        },
        { replace }
      );
    },
    [navigate, location.search]
  );

  const navigationObj = useMemo(
    () => ({
      goToShaprApp: ({
        projectId,
        teamId,
        intent,
        autoOpen,
      }: {
        projectId?: string;
        teamId?: string;
        intent?: DeepLinkIntent;
        autoOpen: boolean;
      }) => {
        if (projectId) {
          tracker.trackDeeplinkOpenInitiated({
            projectId,
            autoOpen,
          });
        }

        const search = new URLSearchParams();
        if (teamId) {
          search.append(DeepLinkSearchParam.TeamID, teamId);
        }
        if (environment !== Environment.PROD) {
          search.append(DeepLinkSearchParam.Environment, environment);
        }
        if (intent) {
          search.append(DeepLinkSearchParam.Intent, intent);
        }

        const projectPath = projectId ? `project/${projectId}` : "";
        const queryString = search.toString();
        const deepLink = `${getShaprURLProtocol()}://${projectPath}${queryString ? `?${queryString}` : ""}`;

        window.location.replace(deepLink);
      },
      goToLandingPage: () => {
        window.location.assign("https://www.shapr3d.com");
      },
      goToDownloadPage: () => {
        window.location.assign(downloadPageUrl + location.search);
      },
      goToPasswordReset: () => {
        navigate("/password-reset" + location.search);
      },
      goToPasswordResetHashError: () => {
        navigate("/password-reset/error");
      },
      goToPasswordResetSuccess: () => {
        navigate("/password-reset/success");
      },
      goToSignUp: (additionalSearchParams?: Record<string, string>) => {
        const url = appendParams("/signup", location.search, additionalSearchParams);
        navigate(url);
      },
      goToSegmentationSurvey: (additionalSearchParams?: Record<string, string>) => {
        const url = appendParams("/segmentation-survey", location.search, additionalSearchParams);
        navigate(url);
      },
      goToProfile: (omitQueryParams?: string[]) => {
        const queryParams = new URLSearchParams(location.search);
        const alwaysOmitParams = [SearchParam.Layout];
        omitQueryParams?.concat?.(alwaysOmitParams);
        omitQueryParams?.forEach?.((name) => queryParams.delete(name));

        navigate({ pathname: "/", search: queryParams.toString() });
      },
      goToSubscription: () => {
        navigate("/subscription");
      },
      goToBilling: (openPaymentModal?: boolean) => {
        const queryParams = new URLSearchParams(location.search);
        if (openPaymentModal) {
          queryParams.set(SearchParam.EditPaymentModalOpen, "true");
        }
        navigate({ pathname: "/billing", search: queryParams.toString() });
      },
      goToTeamPage: ({
        teamID,
        page,
        replace,
      }: {
        teamID: string;
        page: string;
        replace?: boolean;
      }) => {
        navigate(
          { pathname: `/teams/${teamID}${page && "/" + page}`, search: location.search },
          { replace }
        );
      },
      goToTeam: ({
        teamID,
        additionalQueryParams,
        omitQueryParams,
        replace,
      }: {
        teamID: string;
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        replace?: boolean;
      }) => {
        navigate(
          {
            pathname: `/teams/${teamID}`,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
          },
          { replace }
        );
      },
      goToViewLinkListing: ({ teamID }: { teamID: string }) => {
        const search = new URLSearchParams(location.search);
        navigate({
          pathname: `/teams/${teamID}/versions`,
          search: search.toString(),
        });
      },
      goToDefaultPage: goToSharedWithMe,
      goToSharedWithMe,
      goToTeamMembers: ({
        teamID,
        additionalQueryParams,
        omitQueryParams,
        state,
      }: {
        teamID: string;
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        state?: URLState;
      }) => {
        navigate(
          {
            pathname: `/teams/${teamID}/members`,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
          },
          { state }
        );
      },
      goToTeamSubscription: (teamID: string) => {
        navigate(`/teams/${teamID}/subscription`);
      },
      goToTeamBilling: (teamID: string) => {
        navigate(`/teams/${teamID}/billing`);
      },
      goToTeamSettings: ({
        teamID,
        additionalQueryParams,
        omitQueryParams,
        state,
      }: {
        teamID: string;
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        state?: URLState;
      }) => {
        navigate(
          {
            pathname: `/teams/${teamID}/settings`,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
          },
          { state }
        );
      },
      goToSelfServeTeamInvitation: (invitationID: string) => {
        navigate(`/teams/invitation/${invitationID}`);
      },
      goToCheckout: ({
        selectedTier,
        period,
        couponCode,
        withInvite = false,
        omitQueryParams,
        fromAddSeats,
        utmSource,
        returnToURL,
        showPeriodPicker = false,
      }: {
        selectedTier?: SubscriptionTier;
        period?: SubscriptionPeriod;
        couponCode?: string;
        withInvite?: boolean;
        omitQueryParams?: string[];
        fromAddSeats?: boolean;
        utmSource?: string;
        returnToURL?: string;
        showPeriodPicker?: boolean;
      } = {}) => {
        const queryParams = new URLSearchParams(location.search);
        omitQueryParams?.forEach?.((name) => queryParams.delete(name));

        selectedTier && queryParams.set(SearchParam.SubscriptionTier, selectedTier);
        period && queryParams.set(SearchParam.SubscriptionPeriod, period);
        couponCode && queryParams.set(SearchParam.SubscriptionCouponCode, couponCode);
        withInvite && queryParams.set(SearchParam.WithInvite, "true");
        fromAddSeats && queryParams.set(SearchParam.FromAddSeats, "true");
        utmSource && queryParams.set(SearchParam.UTMSource, utmSource);
        showPeriodPicker && queryParams.set(SearchParam.CheckoutShowPeriodPicker, "true");

        if (returnToURL) {
          sessionStorage.setItem(sessionStorageKeys.checkoutReturnUrl, returnToURL);
        } else {
          sessionStorage.removeItem(sessionStorageKeys.checkoutReturnUrl);
        }
        navigate({ pathname: "/checkout", search: queryParams.toString() });
      },
      goToPaymentSuccess: (
        params: {
          additionalQueryParams?: Record<string, string>;
          omitQueryParams?: string[];
        } = {}
      ) => {
        navigate(
          {
            pathname: `/payment-success`,
            search: makeQueryParams(location, params),
          },
          { replace: true }
        );
      },
      goToEduConfirmation: (status: string) => {
        navigate(`/edu/confirmation?status=${status}`);
      },
      openTeamsArticle: () => {
        window.open(teamsArticleURL, "_blank", "noopener noreferrer");
      },
      openTeamsMarketingPage: () => {
        window.open(teamsMarketingPageURL, "_blank", "noopener noreferrer");
      },
      goToZendesk: (zendeskToken: string, returnTo?: string) => {
        const zendeskRedirect = queryString.stringifyUrl({
          url: `${zendeskTokenUrl}/jwt`,
          query: { jwt: zendeskToken, [SearchParam.ReturnTo]: returnTo },
        });
        window.location.assign(zendeskRedirect);
      },
      openUrlWithZendeskAuth: (returnTo: string) => {
        const zendeskRedirect = urlWithZendeskAuth(returnTo);
        window.open(zendeskRedirect, "_blank");
      },
      goToSignIn: (additionalSearchParams?: Record<string, string>) => {
        const url = appendParams("/login", location.search, additionalSearchParams);
        navigate(url);
      },
      goToChangeEmail: () => {
        const queryParams = urlHelper.getQueryParams();
        delete queryParams.hash;

        navigate("/change-email?" + urlHelper.stringify(queryParams));
      },
      goToChangePassword: () => {
        navigate("/change-password");
      },
      goToAccountDeletion: () => {
        const queryParams = urlHelper.getQueryParams();
        navigate("/account-deletion?" + urlHelper.stringify(queryParams));
      },
      goToAccountDeletionSuccess: () => {
        navigate("/account-deletion-success");
      },
      goToInstantTrialConfirmation: () => {
        const queryParams = urlHelper.getQueryParams();
        navigate("/instant-trial-confirmation?" + urlHelper.stringify(queryParams));
      },
      goToClaimVoucher: () => {
        const queryParams = urlHelper.getQueryParams();
        navigate("/claim?" + urlHelper.stringify(queryParams));
      },
      redirectFromSource: (source: PageSource | "embedded" | null) => {
        switch (source) {
          case "embedded":
          case PageSource.Download:
          case PageSource.Pricing:
            navigate("/signup" + location.search, { replace: true });
            return;
          case PageSource.Signin:
          case PageSource.UpdateMac:
          default:
            navigate("/login" + location.search, { replace: true });
            return;
        }
      },
      tryRedirectFromSearchParams: (source?: PageSource): boolean => {
        const redirectUrl = queryString.parse(location.search)[SearchParam.RedirectUrl];
        if (!redirectUrl) {
          return false;
        }

        const decoded = decodeURIComponent(redirectUrl as string);
        const { url, query } = queryString.parseUrl(decoded);
        if (source) {
          query[SearchParam.Source] = source;
        }
        const redirectUrlWithSource = queryString.stringifyUrl({
          url: sanitizeRedirectUrl(url).toString(),
          query,
        });
        window.location.assign(redirectUrlWithSource);
        return true;
      },
      omitQueryParams: (params: string[]) => {
        const queryParams = new URLSearchParams(location.search);
        params?.forEach?.((name) => queryParams.delete(name));
        navigate(
          { pathname: location.pathname, search: queryParams.toString() },
          { replace: true }
        );
      },
      omitHashParams: (params: string[]) => {
        const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
        params?.forEach?.((name) => hashParams.delete(name));
        if (hashParams.toString() != location.hash.replace(/^#/, "")) {
          navigate(
            {
              search: location.search,
              hash: hashParams.toString(),
            },
            { replace: true }
          );
        }
      },
      removePurchaseParamsFromUrl: () => {
        const queryParams = urlHelper.getQueryParams();
        delete queryParams.purchase;
        delete queryParams.period;
        delete queryParams.tier;

        navigate(`/?${urlHelper.stringify(queryParams)}`, { replace: true });
      },
      removeEmailChangeHashParamsFromUrl: () => {
        const queryParams = urlHelper.getQueryParams();
        delete queryParams.hash;

        navigate("/?" + urlHelper.stringify(queryParams), { replace: true });
      },
      goToUpdateNamePage: (
        params: {
          additionalQueryParams?: Record<string, string>;
          omitQueryParams?: string[];
        } = {}
      ) => {
        navigate(
          {
            pathname: `/update-name`,
            search: makeQueryParams(location, params),
          },
          { replace: true }
        );
      },
      goToTwoFactorVerify: ({
        additionalQueryParams,
        omitQueryParams,
        replace,
      }: {
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        replace?: boolean;
      }) => {
        navigate(
          {
            pathname: `/login/2fa/verify`,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
          },
          { replace }
        );
      },
      goToTwoFactorRecovery: ({
        additionalQueryParams,
        omitQueryParams,
        replace,
      }: {
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        replace?: boolean;
      }) => {
        navigate(
          {
            pathname: `/login/2fa/recovery`,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
          },
          { replace }
        );
      },

      goToViewLink: ({
        viewLinkId,
        projectId,
        additionalQueryParams,
        omitQueryParams,
        replace,
        keepHash = false,
      }: {
        viewLinkId: string;
        projectId?: string;
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        replace?: boolean;
        keepHash?: boolean;
      }) => {
        const pathname = projectId ? `/p/${projectId}/v/${viewLinkId}` : `/v/${viewLinkId}`;
        navigate(
          {
            pathname,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
            hash: keepHash ? location.hash : undefined,
          },
          { replace }
        );
      },

      goToDrawing: ({
        viewLinkId,
        projectId,
        drawingId,
        additionalQueryParams,
        omitQueryParams,
        replace,
      }: {
        viewLinkId: string;
        projectId: string;
        drawingId: string;
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        replace?: boolean;
      }) => {
        navigate(
          {
            pathname: `/p/${projectId}/v/${viewLinkId}/drawings/${drawingId}`,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
          },
          { replace }
        );
      },

      goToProject: ({
        projectId,
        additionalQueryParams,
        omitQueryParams,
        replace,
      }: {
        projectId: string;
        additionalQueryParams?: { [K in SearchParam]?: string };
        omitQueryParams?: string[];
        replace?: boolean;
      }) => {
        navigate(
          {
            pathname: `/p/${projectId}`,
            search: makeQueryParams(location, { additionalQueryParams, omitQueryParams }),
          },
          { replace }
        );
      },
    }),
    [navigate, location, urlHelper, goToSharedWithMe, tracker]
  );
  return navigationObj;
};

export default useNavigation;
